import { useContext, useEffect, useState } from "react"
import Logger from "classes/Logger"
import { OderGroupingType } from "types/OrderType"
import { IOrderShippinggroup } from "interfaces/IOrder"
import { groupBy } from "helpers/UtilsHelper"
import { formatDate } from "helpers/FormatDateHelper"
import configEnv from "config/configEnv"
import { STORAGE_SORT_ORDER, TODAY, YERTERDAY } from "helpers/constHelper"
import useCancelToken from "hooks/UseCancelToken"
import { IRangeExtend } from "interfaces/IContentCelendar"
import { TransactionCodeEnum } from "enums/TransactionCodeEnum"
import { AuthContext } from "context/context/AuthContext"
import { GlobalContext } from "context/context/GlobalContext"
import { typeVariantObject } from "types/CommonsTypes"
import { OptionsFilterEnum } from "enums/OptionsFilterEnum"
import { SortOrderEnum } from "enums/OrderEnum"
import useLocalStorage from "hooks/useLocalStorage"
import { EShippinggroupStatus } from "enums/shippinggroupEmun"
import { request_post } from "services/OSRM"
import { ContractorStatusEnum, ContractorTypeEnum } from "enums/ContractorEnum"

const INITIAL_PAGE = 0
const NEW_STATUS = "Nueva" as const

const useGetSgsByStatus = (
  type: string,
  status: string,
  filterOrder: typeVariantObject = { [OptionsFilterEnum.query]: undefined },
) => {
  const [ordersGroup, setOrdersGroup] = useState<OderGroupingType>({})
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(INITIAL_PAGE)
  const [maxPage, setMaxPage] = useState(0)
  const [rangeDate, setRangeDate] = useState<IRangeExtend>()
  const [search, setSearch] = useState<typeVariantObject>(filterOrder)
  const [sort, setSort] = useLocalStorage(STORAGE_SORT_ORDER, SortOrderEnum.desc)
  const [customFilters, setCustomFilters] = useState<typeVariantObject | null>(null)
  const [isEmpty, setIsEmpty] = useState(false)

  const {
    state: { user },
  } = useContext(AuthContext)

  const { errorHander } = useContext(GlobalContext)

  const { isCancel, newCancelToken } = useCancelToken()

  //Make the request to build the current list of SGS
  useEffect(() => {

    const getOrdersGroupByDate = async () => {

      
      const typeValue: ContractorTypeEnum = ContractorTypeEnum[type as keyof typeof ContractorTypeEnum]
      const statusValue: ContractorStatusEnum = ContractorStatusEnum[status as keyof typeof ContractorStatusEnum]

      if (!user?.currentSources) {
        Logger.error("Not found sources")
        return
      }

      const requestBody: any = {
        size: configEnv.maxRecords,
        from: page * configEnv.maxRecords,
        filters: {
          shippingType: "TR",
          channel: get_user_channels(),
          orderCreationDate: rangeDate
          ? {
              from: `${rangeDate.startDate && formatDate(rangeDate.startDate, "yyyy-MM-dd")}T00:00:00`,
              to: `${rangeDate.endDate && formatDate(rangeDate.endDate, "yyyy-MM-dd")}T23:59:59`,
            }
          : undefined,
        },

        sortBy: {
          "status.date": {
            order: sort,
            mode: "min",
          },
        },
        fields: [
          "orderId",
          "orderCreationDate",
          "id",
          "channel",
          "shippingType",
          "salesChannelId",
          "source.id",
          "source.name",
          "target.id",
          "target.name",
          "target.customer",
          "currentStatus",
          "custom.infoElocker",
          "custom.consolidation",
          "custom.retiroMeson",
          "custom.deliveryType",
          "target.source.id",
          "custom.courierDeliveryInfo",
          "custom.infoEntrega",
          "custom.courier",
          "custom.portabilidad",
          "custom.pending_vdi",
          "custom.delivered_vdi",
          "custom.outstandingsinfo",
          "custom.infoCTO",
          "status",
          "trace",
        ],
      }

      if (search.hasOwnProperty(OptionsFilterEnum.query)) {

        requestBody.filters = {
          ...requestBody.filters,
          orderId: search[OptionsFilterEnum.query],
        }

        if(/^SG|sg/.test(search[OptionsFilterEnum.query])) {
          requestBody.filters = {
            ...requestBody.filters,
            id: search[OptionsFilterEnum.query],
            orderId: undefined,
          }
        }
      } else {
        requestBody.filters = {
          ...requestBody.filters,
          ...search,
        }

        if (search.hasOwnProperty(OptionsFilterEnum["source.name"])) {
          requestBody.filters = {
            ...requestBody.filters,
            "source.name": undefined,
            "target.source.name": search[OptionsFilterEnum["source.name"]],
          }
        }

        if (search.hasOwnProperty(OptionsFilterEnum["source.items.custom.series"])) {
          requestBody.filters = {
            ...requestBody.filters,
            "source.items.custom.series": [`*${(search[OptionsFilterEnum["source.items.custom.series"]])}*`],
          }
        }
      }

      /// custom filters
      if (customFilters) {
        requestBody.filters = {
          ...requestBody.filters,
          ...customFilters,
        }
      }

      // TODO: Make interface
      let response: any = {}
      let responseRecords = []
      let responseError: any = null
      let orders: any

      // Block render page
      setLoading(true)

      try {
        requestBody.filters = {
          ...requestBody.filters,
          "source.id": user?.allSources ? undefined : user?.currentSources || [""],
        }

        if (typeValue === ContractorTypeEnum.reception) {
          requestBody.filters = {
            ...requestBody.filters,
            "source.id": undefined,
            "target.source.id": user?.allSources ? undefined : user?.currentSources || [""],
          }
        } 

        requestBody.filters = {
          ...requestBody.filters,
          "custom.infoCTO.orderType": typeValue,
          "custom.infoCTO.statusCTO": statusValue,
          "currentStatus.tag": statusValue !== NEW_STATUS ? [EShippinggroupStatus.delivered] : [EShippinggroupStatus.transit],
        }

        if (typeValue === ContractorTypeEnum.installation && (statusValue === ContractorStatusEnum.incomplete || statusValue === ContractorStatusEnum.new)) {
          requestBody.filters = {
            ...requestBody.filters,
            "currentStatus.tag": [EShippinggroupStatus.confirmation],
          }
        }

        response = await request_post(`search/shipping_groups`, requestBody, newCancelToken())

        response = response.data.message
        responseRecords = response.records
      } catch (error: any) {
        if (isCancel(error)) return
        responseError = error.response?.status
        Logger.error("No results with source.id filter active:", user.currentSources.toString())
      }

      // If doesn't exist orders, return error message
      if (!!responseError) {
        if (responseError === TransactionCodeEnum.notFound) {
          setLoading(false)
          setOrdersGroup(null)
          return
        }
        errorHander?.dispatch({ hasError: true, code: responseError })
      }

      orders = groupBy("orderCreationDateFormat", formatDateOrders(responseRecords))
      setOrdersGroup(orders)
      setMaxPage(Math.ceil(response.pages))
      
      // Detectar si no hay records
      const hasRecords = responseRecords && responseRecords.length > 0
      setIsEmpty(!hasRecords)
      
      setLoading(false)
    }

    getOrdersGroupByDate()

    // eslint-disable-next-line
  }, [status, page, rangeDate, search, sort, customFilters])

  const get_user_channels = () => {
    if (!user?.groups) return [""]
    
    const filteredGroups = user.groups
      .filter((group: any) => group.type === "Channel" && group.id?.startsWith("CTO_"))
      .map((group: any) => group.id)
    
    return filteredGroups.length > 0 ? filteredGroups : [""]
  }

  const onSelectedRangeDate = (range?: IRangeExtend) => {
    setRangeDate(range)
    setPage(INITIAL_PAGE)
  }

  const onChangeSortDate = (sort: SortOrderEnum) => {
    setSort(sort)
    setPage(INITIAL_PAGE)
  }

  const handleChangeCustomFilters = (filter: typeVariantObject | null) => {
    setCustomFilters(filter)
    setPage(INITIAL_PAGE)
  }

  const formatDateOrders = (orders: IOrderShippinggroup[]) => {
    const today = new Date()
    const yesterday = new Date(today)
    yesterday.setDate(yesterday.getDate() - 1)

    const todayFormat = formatDate(today)
    const yesterDayFormat = formatDate(yesterday)

    orders.forEach((order) => {
      let orderDate = formatDate(order.orderCreationDate)
      let indexAwaiting = order.status.findIndex((status) => status.tag === "AWAITING_STORE_CONFIRMATION")
      if (!isNaN(indexAwaiting)) {
        let { date } = order.status[indexAwaiting]
        orderDate = formatDate(date)
      }
      order.orderCreationDateFormat =
        orderDate === todayFormat ? TODAY : orderDate === yesterDayFormat ? YERTERDAY : orderDate
    })

    return orders
  }

  return {
    search,
    ordersGroup,
    loading,
    setPage,
    maxPage,
    page,
    onSelectedRangeDate,
    setSearch,
    onChangeSortDate,
    handleChangeCustomFilters,
    isEmpty,
  }
}

export default useGetSgsByStatus
